/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import React, {useMemo} from 'react';
import {makeStyles} from '@material-ui/core';
import isNil from 'lodash.isnil';
import {ChargeLevelIcon} from '@ion/dls/src/icons/ChargeLevelIcon';
import {toggleThemeStyle} from '../../theme/helpers';
import {dlsAlt, dlsColor} from '../../theme/theme';
import useThemeContext from '../../theme/useThemeContext';
import {DLSTheme} from '../../types/types';
import {
  getChargeLevelColor,
  validateChargeLevel
} from './chargeLevelUtils';

interface StyleProps {
  isBatteryCell?: boolean;
  isCurrentTheme?: boolean;
};

export interface ChargeLevelProps extends StyleProps{
  charge: number;
  isCardVariant?: boolean;
}

const useStyles = makeStyles((theme: DLSTheme) => {
  return {
    chargeText : {
      ...dlsAlt('typography', 'cta2'),
      color      : `${dlsColor('white')} !important`,
      fontWeight : '700 !important',
      padding    : '0 4px'
    },
    cardVariant : {
      color       : `${dlsColor('grey2')}`,
      fontSize    : '14px',
      fontFamily  : 'Roboto',
      fontWeight  : 400,
      paddingLeft : '4px',
      lineHeight  : ({
        isBatteryCell,
        isCurrentTheme
      }: StyleProps) => `${isCurrentTheme ? 'unset' : isBatteryCell ? '21px': '24px'}`
    },
    batteryCell : {
      marginTop : toggleThemeStyle('10%', '0'),
      padding   : '0px !important',
      display   : 'flex'
    }
  };
});

/**
 * ChargeLevel component is used to display the charge level of the asset / device.
 * 
 * ```typescript
 * 
 * import ChargeLevel from '@ion/dls/src/components/chargelevel/ChargeLevel';
 * 
 * <ChargeLevel charge={80} />
 * <ChargeLevel charge={evChargeLevel} />
 * <ChargeLevel charge={deviceChargeLevel} />
 * 
 * ```
 * @param charge - Charge level of the asset / device.
 */
const ChargeLevel = ({
  charge,
  isCardVariant = false,
  isBatteryCell = false
}: ChargeLevelProps) => {
  const {
    isCurrent : isCurrentTheme
  } = useThemeContext();

  const classes     = useStyles({isBatteryCell, isCurrentTheme});
  const chargeLevel = useMemo(() => validateChargeLevel(charge), [charge]);
  
  if (!isNil(charge)) {
    return (
      <>
        <ChargeLevelIcon size={isCardVariant ? isCurrentTheme ? 16 : 22 : 18}
          color={getChargeLevelColor(chargeLevel)}
          outlineColor={isCardVariant ? 'none' : dlsColor('white')} />
        <span className={isCardVariant ? classes.cardVariant : classes.chargeText}>
          {chargeLevel}%
        </span>
      </>
    );
  }
  return null;
};

export default ChargeLevel;
